/**
 * The draft of a VELOX note being written on the document card: one per user,
 * clinic, document, patient and place, in this tab only (`sessionStorage`) — it
 * survives a reload and Back, never the tab, and never reaches another
 * machine. Cleared on a confirmed save and, by the shell, at sign-out.
 * Every access is wrapped: a private window or blocked storage only costs the
 * reload copy; the editor keeps the text.
 * Plan: docs/plans/2026-11-03-velox-notes-written-from-ruby.md
 */

export const NOTE_DRAFT_PREFIX = 'velox-note-draft:v1:';

export interface NoteDraft {
  text: string;
  /** A patient field: VELOX's text when the editing began. A restored draft
   *  compares against this, never against a fresh read. */
  base?: string;
  /** A note: the newest note id the card showed when the draft was first sent. */
  afterNoteId?: number | null;
}

export function noteDraftKey(p: { username: string; clientId: string; documentId: string; patientId: number; place: string }): string {
  return `${NOTE_DRAFT_PREFIX}${[p.username, p.clientId, p.documentId, p.patientId, p.place].map(encodeURIComponent).join(':')}`;
}

function store(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readNoteDraft(key: string, storage: Storage | null = store()): NoteDraft | null {
  try {
    const raw = storage?.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as NoteDraft) : null;
    return parsed && typeof parsed.text === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeNoteDraft(key: string, draft: NoteDraft, storage: Storage | null = store()): void {
  try {
    storage?.setItem(key, JSON.stringify(draft));
  } catch {
    // A full or blocked store costs only the reload copy; the editor keeps the text.
  }
}

export function clearNoteDraft(key: string, storage: Storage | null = store()): void {
  try {
    storage?.removeItem(key);
  } catch {
    // Nothing to do.
  }
}

/** At sign-out: a shared front-desk workstation must not keep anyone's draft. */
export function clearAllNoteDrafts(storage: Storage | null = store()): void {
  try {
    if (!storage) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k?.startsWith(NOTE_DRAFT_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => storage.removeItem(k));
  } catch {
    // Nothing to do.
  }
}

